import { useState, useEffect, useCallback } from 'react';
import { getMooringsByGridId } from '../services/mooringService';
import { getGridById } from '../services/gridService';
import { getHabLightSettings } from '../services/habLightService';

// A mooring is "active" if it's still deployed (no unmoored_at) and has synced
// this calendar year — mirrors the check used on PondsListPage/HABDashboardPage.
const isActiveMooring = (m) => {
  const sync = m?.status?.latest_sync;
  return m?.unmoored_at == null && !!sync && new Date(sync).getFullYear() === new Date().getFullYear();
};

/**
 * Loads buoy-light data for every ACTIVE grid the user can access, grouped by
 * grid. For each grid it resolves `is_owner` + `timezone` (grid detail) and,
 * for each active buoy, the current HAB light settings row (0 or 1).
 *
 * Grids with no active buoys are omitted entirely (we only list active grids).
 *
 * @returns {{ groups: Array, loading: boolean, error: string|null, patchBuoy: Function }}
 *   groups: [{ grid: { id, name, city, region, timezone, is_owner },
 *              buoys: [{ mooringId, buoyId, buoyName, settingsId, settings }] }]
 *   patchBuoy(buoyId, { settingsId, settings }): update one buoy's row in place
 *   after a save, so the UI reflects the persisted values without a refetch.
 */
export function useBuoyLightSettings({ grids, token }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !grids?.length) {
      setGroups([]);
      setLoading(!!token && !grids); // still loading only if we expect data
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Moorings for every accessible grid, in parallel.
        const mooringLists = await Promise.all(
          grids.map(g =>
            getMooringsByGridId(g.id, token)
              .then(res => (Array.isArray(res) ? res : (res?.results || [])))
              .catch(() => [])
          )
        );
        if (cancelled) return;

        // 2. Keep only grids that have at least one active buoy.
        const activeGrids = [];
        grids.forEach((g, i) => {
          const active = (mooringLists[i] || []).filter(isActiveMooring);
          if (active.length) activeGrids.push({ grid: g, moorings: active });
        });

        // 3. For each active grid: grid detail (is_owner + timezone) and each
        //    active buoy's settings row, all in parallel.
        const built = await Promise.all(
          activeGrids.map(async ({ grid, moorings }) => {
            const [detail, ...settingsRows] = await Promise.all([
              getGridById(grid.id, token).catch(() => ({})),
              ...moorings.map(m =>
                m.buoy_id != null
                  ? getHabLightSettings(m.buoy_id, token).then(r => (Array.isArray(r) ? r : [])).catch(() => [])
                  : Promise.resolve([])
              ),
            ]);
            const buoys = moorings.map((m, idx) => {
              const row = settingsRows[idx]?.[0] || null;
              return {
                mooringId: m.id,
                buoyId: m.buoy_id ?? null,
                buoyName: m.buoy || `Buoy ${m.id}`,
                settingsId: row?.id ?? null,
                settings: row,
              };
            });
            return {
              grid: {
                id: grid.id,
                name: grid.name,
                city: detail.city ?? grid.city,
                region: detail.region ?? grid.region,
                timezone: detail.timezone || 'UTC',
                is_owner: !!detail.is_owner,
              },
              buoys,
            };
          })
        );
        if (cancelled) return;
        setGroups(built);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load buoy light settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [grids, token]);

  // After a save, patch the affected buoy's persisted state in place.
  const patchBuoy = useCallback((buoyId, { settingsId, settings }) => {
    setGroups(prev => prev.map(group => ({
      ...group,
      buoys: group.buoys.map(b =>
        b.buoyId === buoyId ? { ...b, settingsId, settings } : b
      ),
    })));
  }, []);

  return { groups, loading, error, patchBuoy };
}
